import React, { FunctionComponent, useState } from 'react';
import {
  CardActionArea,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogTitleProps,
  IconButton,
  SxProps,
  Typography,
} from '@mui/material';

import { Close as CloseIcon } from '@mui/icons-material';

import { useDispatch } from 'react-redux';
import { pdfjs } from 'react-pdf';
import { useAppSelector } from '../../../../hooks';
import { TP_COLORS } from '../../../../constants';

import { getSubTopicState } from '../../../../store/SubTopicSlice';
import { getSessionState } from '../../../../store/SessionSlice';
import { getAssessQuestions } from '../../../../store/QuestionSlice/QuestionApi';
import { getQuestionState } from '../../../../store/QuestionSlice';

import TutorAssessQuestions from '../components/TutorAssessQuestions';
import BasicLoader from '../../../../components/Loaders/BasicLoader';

const dialogTitleProps: DialogTitleProps = {
  sx: {
    padding: '0px',
    fontWeight: '700',
    fontSize: '16px',
    marginBottom: '10px',
  },
};

const cardActionAreaStyles: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: '131px',
  height: '147px',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '20px',
};

interface AddExperienceModalProps {
  open: boolean;
  pageNo: string;
  onClose: () => void;
}

const TutorAssessModal: FunctionComponent<AddExperienceModalProps> = ({
  open,
  pageNo,
  onClose,
}) => {
  const dispatch = useDispatch();
  // Store
  const subTopicState = useAppSelector(getSubTopicState);
  const sessionState = useAppSelector(getSessionState);
  const questionState = useAppSelector(getQuestionState);

  const questions = sessionState.session?.questions;
  const { assessQuestions } = questionState;
  const isSubmitted = assessQuestions.length;

  const resourceUrl =
    (subTopicState.subTopic?.resources?.length &&
      subTopicState.subTopic?.resources[0].downloadUrl) ||
    '';

  // State
  const [isLoading, setLoading] = useState(false);

  const extractTextFromPage = async (pdfDocument, pageNumber) => {
    const page = await pdfDocument.getPage(pageNumber);
    const textContent = await page.getTextContent();
    const textItems = textContent.items.map((item) => item.str);
    return textItems.join(' ');
  };

  const handleSubmit = async (difficulty: string) => {
    const page =
      Number(pageNo) -
        questions.filter((question) => Number(question.pageNo) < Number(pageNo))
          .length || 1;

    if (resourceUrl) {
      const pdf = await pdfjs.getDocument(resourceUrl).promise;
      const extractText = await extractTextFromPage(pdf, Number(page));
      const content = extractText
        .replace(/2018\s*-\s*2020\s*©\s*Tutopiya\s*Pte\s*Ltd/g, '')
        .replace(/\s+/g, ' ')
        .trim();

      setLoading(true);
      await dispatch(
        getAssessQuestions({
          content,
          difficulty,
        })
      );
      setLoading(false);
    }
  };

  return (
    <Dialog
      maxWidth={isSubmitted ? 'md' : 'sm'}
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { padding: '30px', borderRadius: '20px' } }}
    >
      {isLoading ? (
        <BasicLoader />
      ) : (
        <>
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', right: 15, top: 15 }}
          >
            <CloseIcon />
          </IconButton>
          {isSubmitted ? (
            <TutorAssessQuestions />
          ) : (
            <>
              <DialogTitle {...dialogTitleProps}>
                Select Difficulty Level
              </DialogTitle>
              <DialogContentText fontSize="14px" marginBottom="5px">
                Choose Easy, Medium, or Hard to match questions to the
                student&apos;s skill level.
              </DialogContentText>
              <DialogContent
                sx={{
                  display: 'flex',
                  padding: '30px 0px',
                  justifyContent: 'center',
                }}
              >
                <CardActionArea
                  onClick={() => handleSubmit('easy')}
                  sx={{ ...cardActionAreaStyles, backgroundColor: '#E5F9E0' }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/subtopicPage/easy-icon.png`}
                    alt="easy-icon"
                  />
                  <Typography
                    marginTop="28px"
                    sx={{ color: TP_COLORS.green.light }}
                  >
                    Easy
                  </Typography>
                </CardActionArea>

                <CardActionArea
                  onClick={() => handleSubmit('medium')}
                  sx={{
                    ...cardActionAreaStyles,
                    backgroundColor: '#FEFFDC',
                    margin: { xs: '0px 10px', sm: '0px 20px' },
                  }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/subtopicPage/medium-icon.png`}
                    alt="medium-icon"
                  />
                  <Typography
                    marginTop="28px"
                    sx={{ color: TP_COLORS.yellow.light }}
                  >
                    Medium
                  </Typography>
                </CardActionArea>

                <CardActionArea
                  onClick={() => handleSubmit('hard')}
                  sx={{ ...cardActionAreaStyles, backgroundColor: '#FFE4E4' }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/subtopicPage/hard-icon.png`}
                    alt="hard-icon"
                  />
                  <Typography
                    marginTop="28px"
                    sx={{ color: TP_COLORS.orange.light }}
                  >
                    Hard
                  </Typography>
                </CardActionArea>
              </DialogContent>
            </>
          )}
        </>
      )}
    </Dialog>
  );
};

export default TutorAssessModal;
